import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { useAuth, API } from "@/App";
import { AdminLayout } from "./Dashboard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import CustomerProfileSheet from "@/components/CustomerProfileSheet";
import {
    MessageSquare,
    Search,
    Send,
    Globe,
    Sparkles,
    BookOpen,
    BarChart3,
    Inbox,
    Phone,
    Clock,
    TrendingUp,
    Activity,
    AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";

const SOURCE_META = {
    platform_content: { label: "Knowledge base", icon: BookOpen, color: "bg-emerald-500/15 text-emerald-300" },
    ai_generated: { label: "AI (general)", icon: Sparkles, color: "bg-violet-500/15 text-violet-300" },
    web_search: { label: "Web search", icon: Globe, color: "bg-sky-500/15 text-sky-300" },
    subscription: { label: "Subscription cmd", icon: Send, color: "bg-yellow-500/15 text-yellow-200" },
    standings: { label: "Standings", icon: BarChart3, color: "bg-cyan-500/15 text-cyan-200" },
    prediction: { label: "Prediction", icon: TrendingUp, color: "bg-pink-500/15 text-pink-200" },
    error: { label: "Error", icon: AlertTriangle, color: "bg-red-500/15 text-red-300" },
};

const SOURCE_FILTERS = [
    { id: "all", label: "All" },
    { id: "platform_content", label: "Knowledge base" },
    { id: "ai_generated", label: "AI" },
    { id: "web_search", label: "Web" },
    { id: "subscription", label: "Subscriptions" },
    { id: "standings", label: "Standings" },
    { id: "prediction", label: "Predictions" },
];

const fmt = (iso) => {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        return d.toLocaleString("en-GB", {
            timeZone: "Africa/Dar_es_Salaam",
            day: "2-digit", month: "short",
            hour: "2-digit", minute: "2-digit",
        });
    } catch { return "—"; }
};

const SourcePill = ({ source }) => {
    const meta = SOURCE_META[source] || { label: source || "Unknown", icon: Activity, color: "bg-white/10 text-white/70" };
    const Icon = meta.icon;
    return (
        <span
            className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-sm text-[10px] font-bold uppercase tracking-wider ${meta.color}`}
            data-testid={`source-pill-${source}`}
        >
            <Icon className="h-3 w-3" /> {meta.label}
        </span>
    );
};

const Stat = ({ label, value, sub, tone = "default" }) => {
    const toneClass = tone === "warn" ? "text-yellow-300" : tone === "danger" ? "text-red-300" : "text-white";
    return (
        <Card className="bg-paper border-border p-5">
            <div className="text-[11px] uppercase tracking-[0.18em] text-white/50 font-heading">{label}</div>
            <div className={`mt-2 text-3xl font-heading font-black ${toneClass}`} data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>{value}</div>
            {sub && <div className="text-xs text-white/40 mt-1">{sub}</div>}
        </Card>
    );
};

const SmsInteractions = () => {
    const { token } = useAuth();
    const authH = () => ({ Authorization: `Bearer ${token}` });

    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [analytics, setAnalytics] = useState(null);
    const [loading, setLoading] = useState(true);

    const [source, setSource] = useState("all");
    const [phone, setPhone] = useState("");
    const [search, setSearch] = useState("");
    const [days, setDays] = useState(30);

    // Tester
    const [testPhone, setTestPhone] = useState("+255700000000");
    const [testMsg, setTestMsg] = useState("");
    const [testing, setTesting] = useState(false);
    const [lastReply, setLastReply] = useState(null);

    // Customer profile drawer
    const [profilePhone, setProfilePhone] = useState(null);
    const openProfile = (p) => setProfilePhone(p);

    const fetchData = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({ days: String(days), limit: "50" });
            if (source !== "all") params.set("source", source);
            if (phone) params.set("phone", phone);
            if (search) params.set("search", search);
            const [listRes, anaRes] = await Promise.all([
                axios.get(`${API}/admin/sms/interactions?${params.toString()}`, { headers: authH() }),
                axios.get(`${API}/admin/sms/analytics?days=${days}`, { headers: authH() }),
            ]);
            setItems(listRes.data.items || []);
            setTotal(listRes.data.total || 0);
            setAnalytics(anaRes.data);
        } catch (e) {
            toast.error("Failed to load SMS interactions");
        } finally { setLoading(false); }
    };

    useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, [source, days]);

    const runTest = async () => {
        if (!testMsg.trim()) { toast.error("Type a question to send"); return; }
        setTesting(true);
        try {
            const { data } = await axios.post(`${API}/sms/chat`, {
                phone_number: testPhone || "+255700000000",
                message: testMsg.trim(),
            });
            setLastReply(data);
            toast.success(`Reply: ${data.character_count} chars (${data.source})`);
            // Refresh inbox so the new row appears at the top
            fetchData();
        } catch (e) {
            toast.error("Test failed: " + (e?.response?.data?.detail || e.message));
        } finally { setTesting(false); }
    };

    const maxHourly = useMemo(() => {
        if (!analytics?.hourly) return 1;
        return Math.max(1, ...analytics.hourly.map((h) => h.count));
    }, [analytics]);

    return (
        <AdminLayout title="Customer Interactions">
            <div className="space-y-6" data-testid="sms-interactions-page">
                {/* Page intro */}
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                        <h2 className="font-heading text-2xl font-black flex items-center gap-2">
                            <MessageSquare className="h-6 w-6 text-electric" />
                            Customer SMS Interactions
                        </h2>
                        <p className="text-sm text-white/60 mt-1 max-w-2xl">
                            Inbound questions from customers texting Dimbani — answered using the local knowledge base,
                            live AI, or fallback web search. All replies are capped at 160 characters.
                        </p>
                    </div>
                    <div className="flex items-center gap-2" data-testid="window-selector">
                        {[7, 30, 90].map((d) => (
                            <Button
                                key={d}
                                size="sm"
                                variant={days === d ? "default" : "outline"}
                                onClick={() => setDays(d)}
                                data-testid={`days-${d}`}
                                className={days === d ? "bg-electric hover:bg-electric/90" : ""}
                            >
                                {d}d
                            </Button>
                        ))}
                    </div>
                </div>

                {/* Analytics summary */}
                {analytics && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <Stat label="Interactions" value={analytics.total.toLocaleString()} sub={`Last ${analytics.window_days} days`} />
                        <Stat label="Avg reply length" value={`${analytics.avg_response_chars} chars`} sub="Target ≤ 160" />
                        <Stat
                            label="Over 160 chars"
                            value={analytics.responses_over_160}
                            sub={analytics.responses_over_160 > 0 ? "Investigate" : "All within limit"}
                            tone={analytics.responses_over_160 > 0 ? "warn" : "default"}
                        />
                        <Stat label="Unique phones" value={analytics.top_phones.length} sub="Top 10 shown below" />
                    </div>
                )}

                {/* Inbound vs outbound */}
                {analytics && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="inbound-outbound">
                        <Stat label="Inbound (customer Q)" value={analytics.inbound_count || 0} sub="KB / AI / Web answers" />
                        <Stat label="Outbound (push)" value={analytics.outbound_count || 0} sub="For-You + campaigns" />
                        <Stat label="Bulk (CSV)" value={analytics.bulk_sent || 0} sub="From uploaded bases" />
                        <Stat label="For-You digest" value={analytics.for_you_sent || 0} sub="Personalised pushes" />
                    </div>
                )}

                {/* Test widget */}
                <Card className="bg-paper border-border p-5" data-testid="sms-test-widget">
                    <div className="flex items-center gap-2 mb-3">
                        <Sparkles className="h-4 w-4 text-electric" />
                        <span className="font-heading font-bold text-sm uppercase tracking-wider">Test the chat</span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-[200px_1fr_auto] gap-2">
                        <Input
                            placeholder="From phone"
                            value={testPhone}
                            onChange={(e) => setTestPhone(e.target.value)}
                            className="bg-obsidian border-border"
                            data-testid="test-phone-input"
                        />
                        <Input
                            placeholder='Ask anything (e.g. "who won the Ashes 2025")'
                            value={testMsg}
                            onChange={(e) => setTestMsg(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && runTest()}
                            className="bg-obsidian border-border"
                            data-testid="test-message-input"
                        />
                        <Button
                            onClick={runTest}
                            disabled={testing}
                            className="bg-electric hover:bg-electric/90"
                            data-testid="test-send-btn"
                        >
                            <Send className="h-4 w-4 mr-2" />
                            {testing ? "Sending…" : "Send"}
                        </Button>
                    </div>
                    {lastReply && (
                        <div className="mt-4 p-4 bg-obsidian border border-border rounded-sm" data-testid="test-last-reply">
                            <div className="flex items-center justify-between gap-2 mb-2">
                                <SourcePill source={lastReply.source} />
                                <span className="text-xs text-white/50">{lastReply.character_count} / 160 chars</span>
                            </div>
                            <p className="text-white/90 text-sm leading-relaxed">{lastReply.response_sw || lastReply.response}</p>
                        </div>
                    )}
                </Card>

                {/* Filters */}
                <div className="flex flex-wrap items-center gap-2" data-testid="filters">
                    {SOURCE_FILTERS.map((f) => (
                        <Button
                            key={f.id}
                            size="sm"
                            variant={source === f.id ? "default" : "outline"}
                            onClick={() => setSource(f.id)}
                            className={source === f.id ? "bg-electric hover:bg-electric/90" : ""}
                            data-testid={`filter-${f.id}`}
                        >
                            {f.label}
                        </Button>
                    ))}
                    <div className="flex-1 min-w-[200px] flex gap-2">
                        <div className="relative flex-1">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
                            <Input
                                placeholder="Search message or reply…"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                onKeyDown={(e) => e.key === "Enter" && fetchData()}
                                className="pl-9 bg-obsidian border-border"
                                data-testid="filter-search"
                            />
                        </div>
                        <div className="relative w-48">
                            <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
                            <Input
                                placeholder="Phone…"
                                value={phone}
                                onChange={(e) => setPhone(e.target.value)}
                                onKeyDown={(e) => e.key === "Enter" && fetchData()}
                                className="pl-9 bg-obsidian border-border"
                                data-testid="filter-phone"
                            />
                        </div>
                        <Button onClick={fetchData} variant="outline" data-testid="filter-apply">Apply</Button>
                    </div>
                </div>

                {/* Layout: inbox + analytics side */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Inbox */}
                    <Card className="lg:col-span-2 bg-paper border-border p-0 overflow-hidden" data-testid="inbox-card">
                        <div className="p-4 border-b border-border flex items-center justify-between">
                            <span className="font-heading font-bold text-sm uppercase tracking-wider flex items-center gap-2">
                                <Inbox className="h-4 w-4" /> Inbox <span className="text-white/40">({total.toLocaleString()})</span>
                            </span>
                        </div>
                        <div className="divide-y divide-border max-h-[640px] overflow-y-auto" data-testid="inbox-list">
                            {loading && <div className="p-10 text-center text-white/50">Loading…</div>}
                            {!loading && items.length === 0 && (
                                <div className="p-10 text-center text-white/50">No interactions yet for this window.</div>
                            )}
                            {!loading && items.map((it) => (
                                <div key={it.id} className="p-4 hover:bg-subtle/40" data-testid={`inbox-row-${it.id}`}>
                                    <div className="flex items-start justify-between gap-3 mb-2">
                                        <div className="flex items-center gap-2">
                                            <button
                                                onClick={() => openProfile(it.phone_number)}
                                                className="flex items-center gap-1.5 hover:text-electric transition-colors group"
                                                data-testid={`open-profile-${it.phone_number}`}
                                                title="View customer profile"
                                            >
                                                <Phone className="h-3.5 w-3.5 text-white/40 group-hover:text-electric" />
                                                <span className="font-mono text-xs text-white/70 group-hover:text-electric underline-offset-2 group-hover:underline">
                                                    {it.phone_number}
                                                </span>
                                            </button>
                                            <SourcePill source={it.source} />
                                        </div>
                                        <span className="text-xs text-white/40 flex items-center gap-1">
                                            <Clock className="h-3 w-3" /> {fmt(it.created_at)}
                                        </span>
                                    </div>
                                    <div className="text-sm text-white/90 mb-1">
                                        <span className="text-white/40 text-xs mr-2">Q:</span>
                                        {it.message || "—"}
                                    </div>
                                    <div className="text-sm text-white/70 leading-relaxed">
                                        <span className="text-electric text-xs mr-2 font-bold">A:</span>
                                        {it.response}
                                    </div>
                                    <div className="mt-1.5 text-[10px] text-white/40 font-mono">{it.character_count} / 160 chars</div>
                                </div>
                            ))}
                        </div>
                    </Card>

                    {/* Analytics side */}
                    <div className="space-y-4">
                        {analytics && (
                            <>
                                <Card className="bg-paper border-border p-4" data-testid="analytics-sources">
                                    <div className="font-heading font-bold text-sm uppercase tracking-wider mb-3">Sources</div>
                                    <div className="space-y-2">
                                        {analytics.by_source.map((s) => {
                                            const pct = analytics.total ? Math.round((s.count / analytics.total) * 100) : 0;
                                            return (
                                                <div key={s.source} className="space-y-1">
                                                    <div className="flex items-center justify-between text-xs">
                                                        <SourcePill source={s.source} />
                                                        <span className="text-white/70 font-mono">{s.count} ({pct}%)</span>
                                                    </div>
                                                    <div className="h-1.5 bg-obsidian rounded-full overflow-hidden">
                                                        <div className="h-full bg-electric" style={{ width: `${pct}%` }} />
                                                    </div>
                                                </div>
                                            );
                                        })}
                                        {analytics.by_source.length === 0 && (
                                            <div className="text-xs text-white/40">No data yet.</div>
                                        )}
                                    </div>
                                </Card>

                                <Card className="bg-paper border-border p-4" data-testid="analytics-keywords">
                                    <div className="font-heading font-bold text-sm uppercase tracking-wider mb-3">Top topics</div>
                                    <div className="flex flex-wrap gap-1.5">
                                        {analytics.top_keywords.length === 0 && (
                                            <div className="text-xs text-white/40">No data yet.</div>
                                        )}
                                        {analytics.top_keywords.map((k) => (
                                            <Badge key={k.keyword} variant="outline" className="border-border text-white/70" data-testid={`keyword-${k.keyword}`}>
                                                {k.keyword} <span className="ml-1 text-electric font-bold">{k.count}</span>
                                            </Badge>
                                        ))}
                                    </div>
                                </Card>

                                <Card className="bg-paper border-border p-4" data-testid="analytics-phones">
                                    <div className="font-heading font-bold text-sm uppercase tracking-wider mb-3">Most active phones</div>
                                    <div className="space-y-2">
                                        {analytics.top_phones.length === 0 && (
                                            <div className="text-xs text-white/40">No data yet.</div>
                                        )}
                                        {analytics.top_phones.map((p) => (
                                            <button
                                                key={p.phone_number}
                                                onClick={() => openProfile(p.phone_number)}
                                                className="w-full flex items-center justify-between text-xs hover:bg-subtle/40 rounded-sm px-2 py-1.5 -mx-2 transition-colors group"
                                                data-testid={`phone-${p.phone_number}`}
                                                title="View customer profile"
                                            >
                                                <span className="font-mono text-white/80 group-hover:text-electric">{p.phone_number}</span>
                                                <span className="text-electric font-bold">{p.count}</span>
                                            </button>
                                        ))}
                                    </div>
                                </Card>

                                <Card className="bg-paper border-border p-4" data-testid="analytics-hourly">
                                    <div className="font-heading font-bold text-sm uppercase tracking-wider mb-3">Hourly volume (UTC)</div>
                                    <div className="flex items-end gap-0.5 h-24">
                                        {analytics.hourly.map((h) => (
                                            <div
                                                key={h.hour}
                                                className="flex-1 bg-electric/20 hover:bg-electric/60 rounded-t-sm transition-colors relative group"
                                                style={{ height: `${(h.count / maxHourly) * 100}%`, minHeight: "2px" }}
                                                data-testid={`hour-${h.hour}`}
                                                title={`${h.hour}:00 — ${h.count}`}
                                            >
                                                <span className="opacity-0 group-hover:opacity-100 absolute -top-5 left-1/2 -translate-x-1/2 text-[10px] text-white whitespace-nowrap">
                                                    {h.count}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                    <div className="flex justify-between text-[10px] text-white/40 mt-1">
                                        <span>0h</span><span>6h</span><span>12h</span><span>18h</span><span>23h</span>
                                    </div>
                                </Card>
                            </>
                        )}
                    </div>
                </div>
            </div>

            <CustomerProfileSheet
                phone={profilePhone}
                open={!!profilePhone}
                onOpenChange={(o) => { if (!o) setProfilePhone(null); }}
            />
        </AdminLayout>
    );
};

export default SmsInteractions;
